import React from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import CardContainer from 'src/components/common/CardContainer.redesign';

import Button from 'src/components/controls/forms/redesign/FormButton';
import SurfaceTract from 'src/models/SurfaceTract';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import SurfaceTractCard from './SurfaceTractCard';
import { AnyObject, FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import _ from 'lodash';
import { LandSystem } from 'src/models/LandSystem';
import OwnershipContactCategory from 'src/models/OwnershipContactCategory';
import { SelectItem } from 'src/models/common/SelectItem';
import Ownership from 'src/models/tract_detail/Ownership';
import moment from 'moment';
import County from 'src/models/County';
import PagedResultSet from 'src/models/common/PagedResultSet';
import ProjectStaff from 'src/models/ProjectStaff';
import MathUtil from 'src/util/MathUtil';
import { OwnershipCategoryType } from 'src/models/OwnershipCategory';

interface ISurfaceOwnershipListProps {
    landSystem: LandSystem;
    isFormDirty?: boolean;
    ownershipContactCategories: Array<OwnershipContactCategory>;
    categoryTypes: Array<OwnershipCategoryType>;
    counties: Array<County>;
    projectStaff: PagedResultSet<ProjectStaff>;
    makeDirty: () => void;
    openContactNotes: (ownership: Ownership) => void;
    updateTractPage: () => void;
    openOwnershipNotes: (ownership: Ownership, surfaceTractId: number) => void;
}

const SurfaceOwnershipList = React.forwardRef<{}, ISurfaceOwnershipListProps>((props, ref) => {
    let formApi: FormApi = null;
    let submit: (e?: React.SyntheticEvent<HTMLFormElement, Event>) => Promise<AnyObject> = null;

    React.useImperativeHandle(ref, () => ({
        // saveOwnerships
        saveForm: () => {
            submit();
        },
    }));

    const appContext = useSelector((state: IRootState) => state.appContext);
    const [loading, setLoading] = React.useState(true);
    const { get, post, response } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const [surfaceTracts, setSurfaceTracts] = React.useState<Array<SurfaceTract>>([]);
    const [leaseBuyerList, setLeaseBuyerList] = React.useState<SelectItem[]>([]);

    React.useEffect(() => {
        if (!!props.projectStaff) {
            setLeaseBuyerList(props.projectStaff && props.projectStaff.items.length > 0
                ? props.projectStaff.items
                      .map((landman: AnyObject) => new SelectItem(landman.id, landman.user.firstName + ' ' + landman.user.lastName))
                : []
            );
        }
    }, [props]);

    React.useEffect(() => {
        // https://github.com/final-form/final-form/issues/142
        setTimeout(formApi?.reset);
    }, [loading]);

    React.useEffect(() => {
        if (formApi?.getState()?.dirty) {
            props.makeDirty();
        }
    }, [formApi]);

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    const loadSurfaceOwnerships = async () => {
        setLoading(true);
        let surfaceTractCallUrl = Url.tract.getTract(appContext) + '/surfaceTracts';
        let data = await get(surfaceTractCallUrl);

        if (data && data.length > 0) {
            data = data.map((sp: SurfaceTract) => {
                if (sp.recordTypeId) {
                    sp.recordDate = (moment(sp.recordDate).format('YYYY-MM-DD') as unknown as Date);
                }
                // Pre-format each ownership's interest + net acres up front. This used to be
                // done per-card via setTimeout(form.change) on mount, which caused a runaway
                // re-render loop. Doing it here (once) keeps the displayed values identical
                // and leaves the form pristine on load.
                const acres = parseFloat(sp.acres + '');
                const formatOwn = (own: Ownership): Ownership => {
                    const raw = parseFloat(own.decimalInterest + '');
                    (own as AnyObject).decimalInterest = MathUtil.cleanNumber(raw, 10, 0);
                    (own as AnyObject).netAcres = MathUtil.cleanNumber(raw * acres);
                    if (own.additionalOwnerships) {
                        own.additionalOwnerships = own.additionalOwnerships.map(formatOwn);
                    }
                    return own;
                };
                if (sp.ownerships) {
                    sp.ownerships = sp.ownerships.map(formatOwn);
                }
                return sp;
            });
        }

        if (response.ok) {
            setSurfaceTracts(data);
            setLoading(false);
        }
    };

    const formatOwnership = (ownership: Ownership) => {
        ownership.decimalInterest = parseFloat(ownership.decimalInterest + '');
        if (isNaN(ownership.decimalInterest)) {
            delete ownership.decimalInterest;
        }
        // netAcres is pre-formatted to a string on load (MathUtil.cleanNumber) and is not re-parsed
        // anywhere else. The API's Ownership.NetAcres is a double and strict System.Text.Json rejects
        // a JSON string, which fails the entire update-batch body (-> null -> 500). Parse it back to a
        // number here (omit when not numeric) so the payload deserializes.
        (ownership as AnyObject).netAcres = parseFloat((ownership as AnyObject).netAcres + '');
        if (isNaN((ownership as AnyObject).netAcres)) {
            delete (ownership as AnyObject).netAcres;
        }
        ownership.additionalOwnerships?.map((ao: Ownership) => {
            ao = formatOwnership(ao);
        });
        return ownership;
    };

    const onSubmit = async (values: { surfaceTracts: Array<SurfaceTract> }) => {
        const list = values.surfaceTracts.map((so: SurfaceTract) => {
            so.acres = parseFloat(so.acres + '');

            if (isNaN(so.acres)) {
                delete so.acres;
            }

            if (!so.isPartial) {
                so.partialAcreage = 0;
            }

            if (so.ownerships && so.ownerships.length > 0) {
                so.ownerships.map((own: Ownership) => {
                    own = formatOwnership(own);
                });
            }

            return so;
        });

        await post(Url.tract.getTract(appContext) + '/surfaceTracts/update-batch', list);

        if (response.ok) {
            loadSurfaceOwnerships();
        }
    };

    React.useEffect(() => {
        loadSurfaceOwnerships();
    }, []);

    const addSurfaceTract = async (push: (values: AnyObject) => void, surfaceTract: SurfaceTract) => {
        let result = await post(`${Url.tract.getTract(appContext)}/surfaceTracts/attach`, surfaceTract);

        if (response.ok) {
            surfaceTract.number = result;
            push(surfaceTract);
        }
    };

    const createSurfaceTract = async (push: (values: AnyObject) => void) => {
        dialogContext.tract.createTract(
            props.landSystem, 
            appContext.fieldNumber, 
            false,
            props.counties,
            'Surface', 
            (surfaceTract: SurfaceTract) => {
                addSurfaceTract(push, surfaceTract);
            },
            null
        );
    };

    const containerHeader = (push: (value: AnyObject) => void) => {
        return (
            <>
                <fieldset style={{ padding: '.3rem 0', borderWidth: '1px 0 0 0' }}>
                    <legend className="legend">Surface Ownership Information</legend>
                </fieldset>
                <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                <div style={{ textAlign: 'right', marginRight: '1rem' }}>
                    <Button
                        text={`Add Surface ${props.landSystem === LandSystem.Eastern ? 'Parcel' : 'Tract'}`}
                        size="medium"
                        intent="primary"
                        onClick={() => createSurfaceTract(push)}
                        disabled={props.isFormDirty}
                    />
                </div>
            </>
        );
    };

    // tslint:disable-next-line:max-line-length
    const ownershipContactCategoryItems =
        props.ownershipContactCategories && props.ownershipContactCategories.length > 0
            ? props.ownershipContactCategories.map((type: OwnershipContactCategory) => new SelectItem(type.id, type.name))
            : undefined;

    // Stable identity so react-final-form doesn't see a "new" initialValues every render.
    const initialValues = React.useMemo(() => ({ surfaceTracts }), [surfaceTracts]);

    const checkDirty = (dirty: boolean) => {
        if (dirty) {
            props.makeDirty();
        }
    };

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            // Don't re-render the whole 165-card subtree on every field change; individual
            // Fields/FieldArray subscribe to their own state.
            subscription={{ submitting: true, pristine: true, dirty: true }}
            render={({ handleSubmit, form }) => {
                formApi = form;
                submit = handleSubmit;

                return (
                    <>
                        <FormSpy
                            subscription={{ dirty: true }}
                            onChange={(fState) => checkDirty(fState?.dirty)}
                        />
                        <FieldArray name="surfaceTracts">
                            {({ fields }) => {
                                if (fields.length === 0) {
                                    return (
                                        <>
                                            {containerHeader(fields.push)}
                                            <div
                                                style={{
                                                    margin: '1rem',
                                                    padding: '1rem',
                                                    height: '4rem',
                                                    fontWeight: 'bold',
                                                    border: '2px solid red',
                                                    fontSize: '1.1em',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                No Surface Ownerships found
                                            </div>
                                        </>
                                    );
                                }

                                if (loading) {
                                    return (
                                        <>
                                            {containerHeader(fields.push)}
                                            <div
                                                style={{
                                                    margin: '1rem',
                                                    padding: '1rem',
                                                    height: '4rem',
                                                    fontWeight: 'bold',
                                                    border: '2px solid #ccc',
                                                    fontSize: '1.1em',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                Loading ...
                                            </div>
                                        </>
                                    );
                                }

                                return (
                                    <>
                                        {containerHeader(fields.push)}
                                        <CardContainer>
                                            {fields.map((name: string, index: number) => (
                                                <SurfaceTractCard
                                                    key={name}
                                                    formApi={formApi}
                                                    id={fields.value[index].id}
                                                    number={fields.value[index].number}
                                                    surfaceTractAcreage={fields.value[index].acres}
                                                    onSurfaceTractDeleted={() => fields.remove(index)}
                                                    landSystem={props.landSystem}
                                                    namePrefix={`${name}`}
                                                    contactCategories={ownershipContactCategoryItems}
                                                    categoryTypes={props.categoryTypes}
                                                    makeDirty={props.makeDirty}
                                                    openContactNotes={props.openContactNotes}
                                                    updateTractPage={props.updateTractPage}
                                                    leaseBuyers={leaseBuyerList}
                                                    openOwnershipNotes={props.openOwnershipNotes}
                                                />
                                            ))}
                                        </CardContainer>
                                    </>
                                );
                            }}
                        </FieldArray>
                    </>
                );
            }}
        />
    );
});

export default SurfaceOwnershipList;



// WEBPACK FOOTER //
// ./src/components/project/sections/tract/components/SurfaceOwnershipList.tsx